import { useRef, useEffect } from 'react';
import { MapPin, X, Loader2, Wand2 } from 'lucide-react';

// Pin colors by discipline code (for combined multi-layer readability)
const DISC_PIN_COLOR = {
  M: '#0EA5E9', E: '#F59E0B', P: '#10B981', FP: '#EF4444',
  S: '#8B5CF6', A: '#64748B', C: '#A16207', L: '#84CC16', G: '#6366F1',
};
const pinColor = (d) => DISC_PIN_COLOR[String(d || '').toUpperCase()] || '#8B5CF6';

const DISC_COLORS = {
  mechanical: { fill: 'rgba(239,68,68,0.10)', stroke: '#EF4444' },
  hvac: { fill: 'rgba(239,68,68,0.10)', stroke: '#EF4444' },
  electrical: { fill: 'rgba(234,179,8,0.10)', stroke: '#EAB308' },
  plumbing: { fill: 'rgba(34,197,94,0.10)', stroke: '#22C55E' },
  'fire protection': { fill: 'rgba(249,115,22,0.10)', stroke: '#F97316' },
  architectural: { fill: 'rgba(59,130,246,0.10)', stroke: '#3B82F6' },
  structural: { fill: 'rgba(168,85,247,0.10)', stroke: '#A855F7' },
};

function computeCentroid(points) {
  if (!points || points.length === 0) return null;
  let sx = 0, sy = 0;
  for (const p of points) {
    sx += (p.x ?? p[0] ?? 0);
    sy += (p.y ?? p[1] ?? 0);
  }
  return { x: sx / points.length, y: sy / points.length };
}

function midpoint(pts) {
  if (!pts || pts.length < 2) return null;
  const mid = Math.floor(pts.length / 2);
  const a = pts[mid - 1] || pts[0];
  const b = pts[mid] || pts[pts.length - 1];
  return { x: ((a.x ?? 0) + (b.x ?? 0)) / 2, y: ((a.y ?? 0) + (b.y ?? 0)) / 2 };
}

export default function FloorPlanCanvas({
  svgRef, imgUrl, dims, zoom, pan, sheet, setZoom, setPan,
  draggingAsset, setDraggingAsset, handleSvgDrop,
  mode, strokeColor, strokes, activeStroke,
  rooms, assets, pinned, showAnnotations, userAnnotations,
  hoveredAsset, setHoveredAsset,
  overlay_sheets, overlayVisibility,
  calibPoints, measurePoints, measureResult, scaleCalibration,
  takeoffMode, takeoffPoints, measurements,
  unmatched, autoPlotting, handleAutoPlot,
  handleSvgMouseDown, handleSvgMouseMove, handleSvgMouseUp,
  handleTakeoffDoubleClick, handleDeleteStroke,
  showLabels,
  editingMeasurement, editDragIdx,
  handleEditPointMouseDown, handleEditPointMouseMove, handleEditPointMouseUp,
  showHeatmap, heatmapData,
}) {
  const isEditing = !!editingMeasurement;
  const cleanName = (s) => (s ? String(s).replace(/\bnone\b/gi, '').replace(/\s+/g, ' ').trim() : '');
  const svgCursor = isEditing ? (editDragIdx !== null ? 'grabbing' : 'default') : draggingAsset ? 'crosshair' : mode === 'draw' ? 'crosshair' : mode === 'pan' ? 'grab' : 'default';

  const onMouseDown = isEditing ? undefined : handleSvgMouseDown;
  const onMouseMove = isEditing ? handleEditPointMouseMove : handleSvgMouseMove;
  const onMouseUp = isEditing ? handleEditPointMouseUp : handleSvgMouseUp;

  // Zoom interactions: scroll wheel, double-click (in), right-click (out) — anchored to cursor.
  // Refs hold latest zoom/pan so the native (non-passive) wheel listener reads fresh values.
  const containerRef = useRef(null);
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  zoomRef.current = zoom;
  panRef.current = pan;
  const clampZoom = (z) => Math.min(Math.max(z, 0.2), 5);

  const applyZoomAtCursor = (clientX, clientY, factor) => {
    if (!setZoom || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = clientX - (rect.left + rect.width / 2);
    const cy = clientY - (rect.top + rect.height / 2);
    const z0 = zoomRef.current;
    const z1 = clampZoom(z0 * factor);
    if (z1 === z0) return;
    setZoom(z1);
    if (setPan) {
      const d = (1 / z1 - 1 / z0);
      const p0 = panRef.current;
      setPan({ x: p0.x + cx * d, y: p0.y + cy * d });
    }
  };

  // Native wheel listener (React onWheel is passive and can't preventDefault)
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !setZoom) return;
    const onWheel = (e) => {
      if (draggingAsset) return;
      e.preventDefault();
      applyZoomAtCursor(e.clientX, e.clientY, e.deltaY < 0 ? 1.15 : 1 / 1.15);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingAsset, setZoom, setPan]);

  const handleCanvasDoubleClick = (e) => {
    if (!setZoom || mode === 'takeoff' || mode === 'draw' || isEditing) return;
    applyZoomAtCursor(e.clientX, e.clientY, 1.3);
  };
  const handleCanvasContextMenu = (e) => {
    if (!setZoom || mode === 'draw') return;
    e.preventDefault();
    applyZoomAtCursor(e.clientX, e.clientY, 1 / 1.3);
  };

  return (
    <div ref={containerRef} className="flex-1 relative overflow-hidden bg-muted/30"
      onMouseUp={draggingAsset ? handleSvgDrop : undefined}
      onDoubleClick={handleCanvasDoubleClick}
      onContextMenu={handleCanvasContextMenu}
      style={{ cursor: svgCursor }}>

      {/* Pin mode hint */}
      {draggingAsset && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-700 rounded-sm px-4 py-2 text-xs font-heading text-amber-700 dark:text-amber-300 flex items-center gap-2"
          data-testid="fp-drop-hint">
          <MapPin className="w-3.5 h-3.5" /> Click on the floor plan to pin "{draggingAsset.tag || draggingAsset.name}"
          <button onClick={() => setDraggingAsset(null)} className="ml-2 text-amber-500 hover:text-amber-700"><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* Draw mode hint */}
      {mode === 'draw' && !draggingAsset && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-300 dark:border-emerald-700 rounded-sm px-4 py-2 text-xs font-heading text-emerald-700 dark:text-emerald-300 flex items-center gap-2"
          data-testid="fp-draw-hint">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: strokeColor }} />
          Click and drag to draw — {strokes.length} stroke{strokes.length !== 1 ? 's' : ''}
        </div>
      )}

      {imgUrl && (
        <svg ref={svgRef}
          viewBox={`0 0 ${dims.width} ${dims.height}`}
          className="w-full h-full"
          style={{ transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`, transformOrigin: 'center center', userSelect: 'none' }}
          data-testid="floor-plan-svg"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onDoubleClick={mode === 'takeoff' && !isEditing ? handleTakeoffDoubleClick : undefined}>

          {/* Base floor plan image */}
          <image href={imgUrl} x={0} y={0} width={dims.width} height={dims.height} preserveAspectRatio="xMidYMid meet" />

          {/* Heat map overlay */}
          {showHeatmap && heatmapData?.grid?.length > 0 && (
            <g data-testid="heatmap-overlay" opacity={0.55}>
              {heatmapData.grid.map((cell, i) => {
                const intensity = cell.density / (heatmapData.max_density || 1);
                // Green (covered) → Yellow → Red (sparse/gap)
                // High density = green, low = red
                const r = Math.round(255 * (1 - intensity));
                const g = Math.round(200 * intensity);
                const b = 30;
                const alpha = 0.15 + intensity * 0.45;
                return (
                  <rect key={i}
                    x={cell.x} y={cell.y} width={cell.w} height={cell.h}
                    fill={`rgba(${r},${g},${b},${alpha})`}
                    stroke={`rgba(${r},${g},${b},0.3)`}
                    strokeWidth={0.5}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
            </g>
          )}

          {/* Overlay sheets */}
          {(overlay_sheets || []).filter(os => overlayVisibility[os.id]).map(os => {
            const colors = DISC_COLORS[(os.discipline || '').toLowerCase()] || { fill: 'rgba(148,163,184,0.10)', stroke: '#94A3B8' };
            const osImgUrl = `${process.env.REACT_APP_BACKEND_URL}${os.image_url}`;
            return (
              <g key={`overlay-${os.id}`} opacity={0.6}>
                <image href={osImgUrl} x={0} y={0} width={dims.width} height={dims.height} preserveAspectRatio="xMidYMid meet" opacity={0.3} />
                {(os.room_polygons || []).map((rp, i) => {
                  const poly = rp.polygon || [];
                  if (poly.length < 3) return null;
                  return (
                    <polygon key={i} points={poly.map(p => `${p[0]},${p[1]}`).join(' ')}
                      fill={colors.fill} stroke={colors.stroke} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                  );
                })}
              </g>
            );
          })}

          {/* Room polygons */}
          {rooms.map((rp, i) => {
            const poly = rp.polygon || [];
            if (poly.length < 3) return null;
            const centroid = rp.centroid || {};
            return (
              <g key={`room-${i}`}>
                <polygon points={poly.map(p => `${p[0]},${p[1]}`).join(' ')}
                  fill="rgba(59,130,246,0.12)" stroke="#3B82F6" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                {centroid.x && centroid.y && (
                  <text x={centroid.x} y={centroid.y} textAnchor="middle" fill="#3B82F6"
                    fontSize={14} fontFamily="Inter, sans-serif" fontWeight="600" style={{ pointerEvents: 'none' }}>
                    {rp.room_number || rp.room_name || ''}
                  </text>
                )}
              </g>
            );
          })}

          {/* Auto-matched asset markers */}
          {assets.map((m, i) => (
            <g key={`asset-${i}`} onMouseEnter={() => setHoveredAsset(m)} onMouseLeave={() => setHoveredAsset(null)} style={{ cursor: 'pointer' }}>
              <circle cx={m.x} cy={m.y} r={10} fill="#F59E0B" stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" opacity={0.9} />
              <circle cx={m.x} cy={m.y} r={4} fill="#fff" />
              {hoveredAsset?.asset_id === m.asset_id && (
                <g>
                  <rect x={m.x + 14} y={m.y - 20} width={Math.max(140, (m.name || '').length * 7)} height={36} rx={4} fill="#0F172AE0" stroke="#F59E0B" strokeWidth={1} />
                  <text x={m.x + 20} y={m.y - 5} fill="#F59E0B" fontSize={10} fontWeight="bold" fontFamily="Inter">{m.tag || 'Equipment'}</text>
                  <text x={m.x + 20} y={m.y + 8} fill="#94A3B8" fontSize={9} fontFamily="monospace">{m.name || 'Unknown'}</text>
                </g>
              )}
            </g>
          ))}

          {/* Manually pinned assets (coords stored as 0-1 fractions of the sheet) */}
          {pinned.map((m, i) => {
            const px = m.x <= 1 ? m.x * dims.width : m.x;
            const py = m.y <= 1 ? m.y * dims.height : m.y;
            const R = Math.max(8, Math.round(dims.width / 180));
            const fs = Math.round(R * 0.7);
            const c = pinColor(m.discipline);
            return (
            <g key={`pin-${i}`} onMouseEnter={() => setHoveredAsset(m)} onMouseLeave={() => setHoveredAsset(null)} style={{ cursor: 'pointer' }}>
              <circle cx={px} cy={py} r={R} fill={c} stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" opacity={0.9} />
              <circle cx={px} cy={py} r={R * 0.4} fill="#fff" />
              <text x={px} y={py - R - 2} textAnchor="middle" fill={c} fontSize={fs} fontFamily="Inter" fontWeight="bold">{(m.discipline || 'PIN').toUpperCase()}</text>
              {hoveredAsset?.asset_id === m.asset_id && (
                <g>
                  <rect x={px + R + 2} y={py - R} width={Math.max(140, (m.name || '').length * 7)} height={R * 2.2} rx={4} fill="#0F172AE0" stroke={c} strokeWidth={1} />
                  <text x={px + R + 8} y={py - R * 0.2} fill={c} fontSize={fs} fontWeight="bold" fontFamily="Inter">{m.tag || cleanName(m.name) || 'Equipment'}</text>
                  <text x={px + R + 8} y={py + R * 0.6} fill="#94A3B8" fontSize={fs * 0.9} fontFamily="monospace">{cleanName(m.name) || '—'}</text>
                </g>
              )}
            </g>
            );
          })}

          {/* Polygon annotations */}
          {showAnnotations && userAnnotations.map((ann, i) => {
            const poly = ann.polygon || [];
            if (poly.length < 3) return null;
            return (
              <polygon key={`ann-${i}`} points={poly.map(p => `${p[0]},${p[1]}`).join(' ')}
                fill="rgba(16,185,129,0.15)" stroke="#10B981" strokeWidth={2} strokeDasharray="6,3"
                vectorEffect="non-scaling-stroke" style={{ cursor: 'pointer' }} />
            );
          })}

          {/* Freehand strokes */}
          {showAnnotations && strokes.map(stroke => (
            <polyline key={stroke.id}
              data-testid={`fp-stroke-${stroke.id}`}
              points={stroke.points.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={stroke.color}
              strokeWidth={stroke.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              style={{ cursor: mode === 'delete' ? 'pointer' : 'default', pointerEvents: mode === 'delete' ? 'auto' : 'none' }}
              onClick={(e) => {
                if (mode === 'delete') { e.stopPropagation(); handleDeleteStroke(stroke.id); }
              }}
            />
          ))}

          {/* Active stroke (being drawn) */}
          {activeStroke && activeStroke.points.length > 1 && (
            <polyline
              data-testid="fp-active-stroke"
              points={activeStroke.points.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={activeStroke.color}
              strokeWidth={activeStroke.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              opacity={0.7}
              style={{ pointerEvents: 'none' }}
            />
          )}

          {/* Calibration points */}
          {calibPoints.map((pt, i) => (
            <g key={`calib-${i}`}>
              <circle cx={pt.x} cy={pt.y} r={6} fill="none" stroke="#F59E0B" strokeWidth={2} vectorEffect="non-scaling-stroke" />
              <line x1={pt.x - 10} y1={pt.y} x2={pt.x + 10} y2={pt.y} stroke="#F59E0B" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              <line x1={pt.x} y1={pt.y - 10} x2={pt.x} y2={pt.y + 10} stroke="#F59E0B" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            </g>
          ))}
          {calibPoints.length === 2 && (
            <line x1={calibPoints[0].x} y1={calibPoints[0].y} x2={calibPoints[1].x} y2={calibPoints[1].y}
              stroke="#F59E0B" strokeWidth={2} strokeDasharray="8,4" vectorEffect="non-scaling-stroke" />
          )}

          {/* Measurement points & line */}
          {measurePoints.map((pt, i) => (
            <g key={`meas-${i}`}>
              <circle cx={pt.x} cy={pt.y} r={5} fill="none" stroke="#10B981" strokeWidth={2} vectorEffect="non-scaling-stroke" />
              <circle cx={pt.x} cy={pt.y} r={2} fill="#10B981" />
            </g>
          ))}
          {measurePoints.length === 2 && (
            <g>
              <line x1={measurePoints[0].x} y1={measurePoints[0].y} x2={measurePoints[1].x} y2={measurePoints[1].y}
                stroke="#10B981" strokeWidth={2} strokeDasharray="6,3" vectorEffect="non-scaling-stroke" />
              {measureResult && (
                <text
                  x={(measurePoints[0].x + measurePoints[1].x) / 2}
                  y={(measurePoints[0].y + measurePoints[1].y) / 2 - 12}
                  textAnchor="middle" fill="#10B981"
                  fontSize={14} fontFamily="Inter, monospace" fontWeight="700"
                  style={{ pointerEvents: 'none' }}>
                  {measureResult.display}
                </text>
              )}
            </g>
          )}

          {/* Scale reference line */}
          {scaleCalibration?.calibrated && scaleCalibration.point1 && mode !== 'calibrate' && (
            <g opacity={0.3}>
              <line
                x1={scaleCalibration.point1.x} y1={scaleCalibration.point1.y}
                x2={scaleCalibration.point2.x} y2={scaleCalibration.point2.y}
                stroke="#F59E0B" strokeWidth={1} strokeDasharray="4,4" vectorEffect="non-scaling-stroke" />
            </g>
          )}

          {/* Takeoff: active polygon/polyline/count markers */}
          {mode === 'takeoff' && takeoffPoints.length > 0 && (
            <g>
              {takeoffMode === 'area' && takeoffPoints.length >= 2 && (
                <polygon
                  points={takeoffPoints.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="rgba(16, 185, 129, 0.15)" stroke="#10B981" strokeWidth={2}
                  strokeDasharray="6,3" vectorEffect="non-scaling-stroke" />
              )}
              {takeoffMode === 'linear' && takeoffPoints.length >= 2 && (
                <polyline
                  points={takeoffPoints.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none" stroke="#3B82F6" strokeWidth={2.5}
                  strokeDasharray="8,4" vectorEffect="non-scaling-stroke" />
              )}
              {takeoffPoints.map((pt, i) => (
                <g key={`tp-${i}`}>
                  <circle cx={pt.x} cy={pt.y} r={takeoffMode === 'count' ? 8 : 4}
                    fill={takeoffMode === 'count' ? '#8B5CF6' : takeoffMode === 'area' ? '#10B981' : '#3B82F6'}
                    stroke="white" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                  {takeoffMode === 'count' && (
                    <text x={pt.x} y={pt.y + 4} textAnchor="middle" fill="white"
                      fontSize={9} fontWeight="700" fontFamily="monospace" style={{ pointerEvents: 'none' }}>
                      {i + 1}
                    </text>
                  )}
                </g>
              ))}
            </g>
          )}

          {/* Saved measurements overlay */}
          {measurements.filter(m => m.points?.length > 0 && m.status !== 'proposed').map(m => (
            <g key={m.id} opacity={0.5}>
              {m.type === 'area' && m.points.length >= 3 && (
                <polygon
                  points={m.points.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="rgba(16, 185, 129, 0.08)" stroke="#10B981" strokeWidth={1}
                  strokeDasharray="4,2" vectorEffect="non-scaling-stroke" />
              )}
              {m.type === 'linear' && m.points.length >= 2 && (
                <polyline
                  points={m.points.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none" stroke="#3B82F6" strokeWidth={1.5}
                  strokeDasharray="4,2" vectorEffect="non-scaling-stroke" />
              )}
              {m.type === 'count' && m.points.map((pt, i) => (
                <circle key={i} cx={pt.x} cy={pt.y} r={5} fill="#8B5CF6" opacity={0.5} />
              ))}
            </g>
          ))}

          {/* AI Proposed measurements overlay (pulsing amber) */}
          {measurements.filter(m => m.points?.length > 0 && m.status === 'proposed').map(m => {
            // If this measurement is being edited, use editingMeasurement.points instead
            const isBeingEdited = editingMeasurement?.id === m.id;
            if (isBeingEdited) return null; // rendered separately below
            return (
            <g key={`proposed-${m.id}`} opacity={0.7} data-testid={`proposed-overlay-${m.id}`}>
              {m.type === 'area' && m.points.length >= 3 && (
                <polygon
                  points={m.points.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="rgba(245, 158, 11, 0.12)" stroke="#F59E0B" strokeWidth={2}
                  strokeDasharray="8,4" vectorEffect="non-scaling-stroke">
                  <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2s" repeatCount="indefinite" />
                </polygon>
              )}
              {m.type === 'linear' && m.points.length >= 2 && (
                <polyline
                  points={m.points.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none" stroke="#F59E0B" strokeWidth={2.5}
                  strokeDasharray="8,4" vectorEffect="non-scaling-stroke">
                  <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2s" repeatCount="indefinite" />
                </polyline>
              )}
              {m.type === 'count' && m.points.map((pt, i) => (
                <g key={i}>
                  <circle cx={pt.x} cy={pt.y} r={7} fill="#F59E0B" opacity={0.7}>
                    <animate attributeName="r" values="6;9;6" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={pt.x} cy={pt.y} r={3} fill="white" />
                </g>
              ))}
            </g>
            );
          })}

          {/* Editing measurement overlay with draggable handles */}
          {editingMeasurement && (() => {
            const pts = editingMeasurement.points;
            const m = measurements.find(mm => mm.id === editingMeasurement.id);
            const mtype = m?.type || 'area';
            const color = mtype === 'area' ? '#10B981' : mtype === 'linear' ? '#3B82F6' : '#8B5CF6';
            return (
              <g data-testid="editing-measurement-overlay">
                {mtype === 'area' && pts.length >= 3 && (
                  <polygon points={pts.map(p => `${p.x},${p.y}`).join(' ')}
                    fill={`${color}15`} stroke={color} strokeWidth={2.5}
                    strokeDasharray="6,3" vectorEffect="non-scaling-stroke" />
                )}
                {mtype === 'linear' && pts.length >= 2 && (
                  <polyline points={pts.map(p => `${p.x},${p.y}`).join(' ')}
                    fill="none" stroke={color} strokeWidth={3}
                    strokeDasharray="6,3" vectorEffect="non-scaling-stroke" />
                )}
                {pts.map((pt, i) => (
                  <g key={`edit-handle-${i}`} data-testid={`edit-handle-${i}`}
                    onMouseDown={(e) => { e.stopPropagation(); handleEditPointMouseDown(i); }}
                    style={{ cursor: editDragIdx === i ? 'grabbing' : 'grab' }}>
                    <circle cx={pt.x} cy={pt.y} r={12} fill="transparent" />
                    <circle cx={pt.x} cy={pt.y} r={7} fill="white" stroke={color} strokeWidth={2.5} vectorEffect="non-scaling-stroke">
                      {editDragIdx === i && <animate attributeName="r" values="7;9;7" dur="0.5s" repeatCount="indefinite" />}
                    </circle>
                    <circle cx={pt.x} cy={pt.y} r={3} fill={color} />
                    <text x={pt.x + 12} y={pt.y + 4} fill={color} fontSize={9} fontWeight="700" fontFamily="JetBrains Mono"
                      style={{ pointerEvents: 'none' }}>
                      {i + 1}
                    </text>
                  </g>
                ))}
              </g>
            );
          })()}

          {/* Measurement dimension labels (toggleable layer) */}
          {showLabels && measurements.filter(m => m.points?.length > 0 && m.real_value).map(m => {
            const label = `${m.real_value} ${m.unit || ''}`.trim();
            let pos = null;
            let color = '#10B981';

            if (m.type === 'area' && m.points.length >= 3) {
              pos = computeCentroid(m.points);
              color = '#10B981';
            } else if (m.type === 'linear' && m.points.length >= 2) {
              pos = midpoint(m.points);
              color = '#3B82F6';
            } else if (m.type === 'count' && m.points.length > 0) {
              pos = { x: m.points[0].x, y: m.points[0].y - 16 };
              color = '#8B5CF6';
            }

            if (!pos) return null;
            return (
              <g key={`label-${m.id}`} data-testid={`measurement-label-${m.id}`} style={{ pointerEvents: 'none' }}>
                <rect x={pos.x - label.length * 3.5 - 4} y={pos.y - 9} width={label.length * 7 + 8} height={16}
                  rx={3} fill="#0F172AE0" stroke={color} strokeWidth={0.5} />
                <text x={pos.x} y={pos.y + 3} textAnchor="middle" fill={color}
                  fontSize={10} fontWeight="700" fontFamily="JetBrains Mono, monospace">
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      )}

      {/* Sheet info badge */}
      <div className="absolute top-3 left-3 bg-card/90 backdrop-blur-sm border border-border rounded-sm px-3 py-1.5 z-10">
        <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">
          {sheet?.sheet_number} — {sheet?.sheet_name}
        </p>
        <p className="text-[8px] text-muted-foreground">{sheet?.discipline}</p>
      </div>

      {/* Stats */}
      <div className="absolute bottom-3 left-3 flex gap-2 z-10">
        <span className="bg-card/90 backdrop-blur-sm border border-border rounded-sm px-2 py-1 text-[9px] font-mono text-blue-500">{rooms.length} rooms</span>
        <span className="bg-card/90 backdrop-blur-sm border border-border rounded-sm px-2 py-1 text-[9px] font-mono text-amber-500">{assets.length} auto-mapped</span>
        <span className="bg-card/90 backdrop-blur-sm border border-border rounded-sm px-2 py-1 text-[9px] font-mono text-purple-500">{pinned.length} pinned</span>
        {strokes.length > 0 && (
          <span className="bg-card/90 backdrop-blur-sm border border-border rounded-sm px-2 py-1 text-[9px] font-mono text-emerald-500" data-testid="fp-stroke-count">{strokes.length} strokes</span>
        )}
        {unmatched.length > 0 && (
          <span className="bg-card/90 backdrop-blur-sm border border-border rounded-sm px-2 py-1 text-[9px] font-mono text-rose-500">{unmatched.length} unmatched</span>
        )}
        {unmatched.length > 0 && (
          <button onClick={handleAutoPlot} disabled={autoPlotting}
            data-testid="auto-plot-btn"
            className="bg-card/90 backdrop-blur-sm border border-amber-700 rounded-sm px-2.5 py-1 text-[9px] font-mono text-amber-400 hover:bg-amber-950/30 transition-colors disabled:opacity-50 flex items-center gap-1">
            {autoPlotting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
            Auto-Plot
          </button>
        )}
      </div>
    </div>
  );
}
